import React, { useCallback, useEffect, useRef } from 'react';

import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import { SideNavType } from 'common/utils/navigation';
import TabBar from '../TabBar';
import { trackSideNavTabClick, trackSideNavTabsView } from '../tracking';
import type { ContentNavigationTab } from '../types';
import { getCascadingNavigationData, getNavigationData, sanitizeTabLabel } from '../utils';
import type { UseTabbedNavigationResult } from './useTabbedNavigation';
import { useTabbedNavigation } from './useTabbedNavigation';

interface UseSideNavTabsParams {
  tabs: ContentNavigationTab[];
  withSideNavTabs: boolean;
  type: SideNavType;
  isOpen: boolean;
  currentNavigation: number[];
  selectedItem: number | null;
  navigation: GraphqlNavigationItemProps | GraphqlNavigationItemProps[];
  fetchedNavigation: GraphqlNavigationItemProps;
  closeHandler: () => void;
  setCurrentNavigation: (nav: number[]) => void;
  setSelectedItem: (item: number | null) => void;
}

const useTabsViewTracking = (isOpen: boolean, activeTabLabel: string | undefined) => {
  const hasTrackedOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && activeTabLabel && !hasTrackedOpenRef.current) {
      trackSideNavTabsView(activeTabLabel);
      hasTrackedOpenRef.current = true;
    }
    if (!isOpen) {
      hasTrackedOpenRef.current = false;
    }
  }, [isOpen, activeTabLabel]);
};

interface GetMobileNavigationParams {
  tabsState: UseTabbedNavigationResult | null;
  currentNavigation: number[];
  isAtRootLevel: boolean;
  fallback: GraphqlNavigationItemProps | GraphqlNavigationItemProps[];
}

const getMobileNavigation = ({
  tabsState,
  currentNavigation,
  isAtRootLevel,
  fallback,
}: GetMobileNavigationParams) => {
  if (!tabsState) return fallback;
  if (isAtRootLevel) return tabsState.navigation;

  return getNavigationData(currentNavigation, tabsState.navigation);
};

export const useSideNavTabs = ({
  tabs,
  withSideNavTabs,
  type,
  isOpen,
  currentNavigation,
  selectedItem,
  navigation,
  fetchedNavigation,
  closeHandler,
  setCurrentNavigation,
  setSelectedItem,
}: UseSideNavTabsParams) => {
  const isEnabled = withSideNavTabs && type === SideNavType.Global;
  const tabsState = useTabbedNavigation({ tabs, isEnabled });

  const isTabsEnabled = tabsState !== null;
  const isAtRootLevel = currentNavigation.length === 0 && selectedItem === null;
  const activeTabLabel = tabsState?.activeTab.label;
  const isDefaultTab = tabsState?.isDefaultTab ?? true;
  const resetTabs = tabsState?.resetTabs;

  useTabsViewTracking(isOpen, activeTabLabel);

  const handleClose = useCallback(() => {
    closeHandler();
    resetTabs?.();
  }, [closeHandler, resetTabs]);

  const handleTabChange = useCallback(
    (label: string, index: number) => {
      if (!tabsState) return;
      tabsState.setActiveTab(label);
      setCurrentNavigation([]);
      setSelectedItem(null);
      trackSideNavTabClick(label, index, tabsState.tabs.length);
    },
    [tabsState, setCurrentNavigation, setSelectedItem]
  );

  const TabBarComponent = isTabsEnabled && (
    <TabBar
      tabs={tabsState.tabs}
      activeTabIndex={tabsState.activeTabIndex}
      onTabChange={handleTabChange}
    />
  );

  const desktopNavigation = isTabsEnabled
    ? getCascadingNavigationData({
        currentNavigation,
        fetchedNavigation: tabsState.navigation,
        selectedItem,
      })
    : navigation;

  const desktopNavLength = (desktopNavigation as GraphqlNavigationItemProps[]).length;

  const mobileNavigation = getMobileNavigation({
    tabsState,
    currentNavigation,
    isAtRootLevel,
    fallback: navigation,
  });

  const headerNavigation = isTabsEnabled ? tabsState.navigation : fetchedNavigation;
  const sanitizedLabel = isTabsEnabled ? sanitizeTabLabel(tabsState.activeTab.label) : '';
  const tabPanelProps = isTabsEnabled
    ? {
        id: `tabpanel-${sanitizedLabel}`,
        role: 'tabpanel' as const,
        'aria-labelledby': `tab-${sanitizedLabel}`,
      }
    : {};
  const hideMobileTitle = isTabsEnabled;

  return {
    isTabsEnabled,
    isAtRootLevel,
    activeTabLabel,
    isDefaultTab,
    handleClose,
    TabBarComponent,
    desktopNavigation,
    desktopNavLength,
    mobileNavigation,
    headerNavigation,
    tabPanelProps,
    hideMobileTitle,
  };
};
